import { assertNever } from "@/lib/utils";

/**
 * Every audit action the server can record, mirroring the `Action` constants in
 * `server/internal/audit/*.go`. Listing them here (rather than treating
 * `log.action` as an opaque string) is what lets `staticActionPhrase` be an
 * exhaustive switch: adding a Go action without a phrase here fails type-check
 * the moment the string lands in this list.
 */
export const AUDIT_ACTIONS = [
  "slack-directory-connection:authorize",
  "slack-directory-connection:disconnect",
  "slack-directory-connection:sync",
  "slack-identity-mapping:confirm",
  "slack-identity-mapping:reassign",
  "slack-identity-mapping:reconfirm",
  "slack-identity-mapping:unmap",
  "access_challenge:resolve",
  "access_member:revoke_sign_ins",
  "access_member:update_role",
  "access_role:create",
  "access_role:delete",
  "access_role:update",
  "agent:connection_block_create",
  "agent:connection_block_delete",
  "agent:create",
  "agent:delete",
  "agent:owner_loss",
  "agent:policy_grant_create",
  "agent:policy_grant_delete",
  "agent:policy_grant_update",
  "agent:reassign",
  "agent:rename",
  "agent:resume",
  "agent:revoke",
  "agent:suspend",
  "agent:transfer",
  "ai_integration:delete",
  "ai_integration:retry_schedule",
  "ai_integration:update_schedule",
  "ai_integration:upsert",
  "ai_tool_decision:set",
  "api_key:create",
  "api_key:revoke",
  "api_key:update_policy",
  "asset:create",
  "auth-client:attach-mcp-server",
  "auth-client:create",
  "auth-client:detach-mcp-server",
  "auth-client:disable",
  "auth-client:enable",
  "auth-client:update",
  "auth-client:update-audience",
  "auth-client:update-headers",
  "assistant:identity_provision",
  "assistant:tool_call",
  "assistant:update_execution_profile",
  "aws_iam_credential:create",
  "aws_iam_credential:delete",
  "aws_iam_credential:update",
  "aws_kms_key:create",
  "aws_kms_key:delete",
  "aws_kms_key:update",
  "billing_metadata:cancel_stripe_subscription",
  "billing_metadata:create_stripe_checkout",
  "billing_metadata:create_stripe_portal",
  "billing_metadata:resume_stripe_subscription",
  "billing_metadata:update",
  "catalog-promotion:archive",
  "catalog-promotion:reject",
  "catalog-promotion:restore",
  "catalog-promotion:retry-docs",
  "chat_analysis_settings:upsert",
  "chat_session:access",
  "chat_session:handoff_export",
  "chat_session:move",
  "chat_session:recall",
  "custom_domains:create",
  "custom_domains:delete",
  "custom_domains:update",
  "dashboard:create",
  "dashboard:delete",
  "dashboard:layout",
  "dashboard:update",
  "data_export_route:create",
  "data_export_route:delete",
  "data_export_route:pause",
  "data_export_route:resume",
  "data_export_route:update",
  "deployments:create",
  "deployments:evolve",
  "deployments:redeploy",
  "device_agent_ai_scan_target:create",
  "device_agent_ai_scan_target:delete",
  "device_agent_ai_scan_target:update",
  "device_integration:delete",
  "device_integration:retry_schedule",
  "device_integration:update_schedule",
  "device_integration:upsert",
  "directory_review_item:approve",
  "directory_review_item:dismiss",
  "directory_review_item:opt_out",
  "directory_review_settings:update",
  "directory_role_mapping:delete",
  "directory_role_mapping:set",
  "environment:create",
  "environment:delete",
  "environment:update",
  "gcp_iam_credential:create",
  "gcp_iam_credential:delete",
  "gcp_iam_credential:update",
  "gcp_kms_key:create",
  "gcp_kms_key:delete",
  "gcp_kms_key:update",
  "identity-provider-connection:create",
  "identity-provider-connection:record-agent",
  "identity-provider-connection:replace-client-secret",
  "identity-provider-connection:set-setup-method",
  "identity-provider-connection:revoke",
  "identity-provider-connection:submit-client-id",
  "identity-provider-connection:sync-applications",
  "identity-provider-connection:verify",
  "internal_session_issuer:disable",
  "internal_session_issuer:enable",
  "issuance-selection-profile:create",
  "issuance-selection-profile:delete",
  "issuance-selection-profile:update",
  "json_web_key:activate",
  "json_web_key:delete",
  "json_web_key:publish",
  "json_web_key:retire",
  "json_web_key:revoke",
  "json_web_key_set:create",
  "json_web_key_set:delete",
  "json_web_key_set:update",
  "killswitch:activate",
  "killswitch:change",
  "killswitch:deactivate",
  "killswitch:expire",
  "litellm_instance:create",
  "litellm_instance:revoke",
  "litellm_instance:rotate_key",
  "mcp-endpoint:create",
  "mcp-endpoint:delete",
  "mcp-endpoint:update",
  "mcp-server:create",
  "mcp-server:delete",
  "mcp-server:promote-catalogue",
  "mcp-server:update",
  "mcp-server:update-scope-pin",
  "mcp-server:update-tool-metadata",
  "mcp-server:update-upstream-auth-requirement",
  "mcp_approval_request:approve",
  "mcp_approval_request:create",
  "mcp_approval_request:deny",
  "mcp_approval_request:evidence_changed",
  "mcp_approval_request:research_start",
  "mcp_approval_request:supersede",
  "mcp_metadata:update",
  "meta-mcp:add_member",
  "meta-mcp:create",
  "meta-mcp:delete",
  "meta-mcp:remove_member",
  "meta-mcp:update",
  "meta-mcp:update_member",
  "model_provider_key:delete",
  "model_provider_key:upsert",
  "network_ingress:create",
  "network_ingress:delete",
  "network_ingress:rotate_credentials",
  "network_ingress:update",
  "openrouter-key:disable",
  "openrouter-key:enable",
  "openrouter-key:set_spend_cap",
  "organization:device_agent_configuration_updated",
  "organization:disabled",
  "organization:enabled",
  "organization:whitelist_updated",
  "organization:account_type_changed",
  "organization:inference_key_repaired",
  "organization:enterprise_trial_armed",
  "organization:enterprise_trial_converted",
  "organization:enterprise_trial_demoted",
  "organization:enterprise_trial_end_changed",
  "organization:enterprise_trial_extended",
  "organization:enterprise_trial_rearmed",
  "organization:enterprise_trial_started",
  "organization:hooks_fail_open_disabled",
  "organization:hooks_fail_open_enabled",
  "organization:onboarding_updated",
  "organization:onboarding_stack_updated",
  "organization:onboarding_playbook_assigned",
  "organization:onboarding_playbook_unassigned",
  "organization:partner_signup_attributed",
  "organization:promotion_redeemed",
  "organization:payg_activated",
  "organization:payg_deactivated",
  "organization:product_feature_disabled",
  "organization:product_feature_enabled",
  "organization:setup_task_updated",
  "organization:webhooks_disabled",
  "organization:webhooks_enabled",
  "organization_invitation:create",
  "organization_invitation:revoke",
  "organization_invitation:update_role",
  "otel_destination:create",
  "otel_destination:delete",
  "okta-resource-connection:confirm",
  "okta-resource-connection:reset",
  "okta-resource-connection:observe",
  "okta-server-suggestion:dismiss",
  "okta-server-suggestion:restore",
  "otel_destination:update",
  "platform-mcp-diagnostics:attribution_read",
  "platform-mcp-diagnostics:user_status_read",
  "platform-mcp-registration:create",
  "platform-mcp-registration:handoff_issue",
  "platform-mcp-registration:handoff_redeem",
  "plugin:assignments_set",
  "plugin:create",
  "plugin:delete",
  "plugin:marketplace_settings_update",
  "plugin:publish",
  "plugin:server_add",
  "plugin:server_remove",
  "plugin:server_update",
  "plugin:update",
  "project:create",
  "project:delete",
  "project:update",
  "query:create",
  "query:delete",
  "query:update",
  "remote-mcp-server-header:create",
  "remote-mcp-server-header:delete",
  "remote-mcp-server-header:update",
  "remote-mcp:create",
  "remote-mcp:delete",
  "remote-mcp:update",
  "remote-session-client:attach-json-web-key-set",
  "remote-session-client:attach-user-session-issuer",
  "remote-session-client:create",
  "remote-session-client:delete",
  "remote-session-client:detach-json-web-key-set",
  "remote-session-client:detach-mcp-server",
  "remote-session-client:detach-user-session-issuer",
  "remote-session-client:disable-identity-chaining",
  "remote-session-client:duplicate",
  "remote-session-client:enable-identity-chaining",
  "remote-session-client:revoke-sessions",
  "remote-session-client:update",
  "remote-session-client:update-identity-chaining-scopes",
  "remote-session-issuer:create",
  "remote-session-issuer:delete",
  "remote-session-issuer:migrate",
  "remote-session-issuer:update",
  "remote-session-issuer:update_ema_providers",
  "remote-session:attach",
  "remote-session:delete",
  "remote-session:detach",
  "remote-session:refresh",
  "risk_exclusion:create",
  "risk_exclusion:delete",
  "risk_exclusion:update",
  "risk_policy:approval_approve",
  "risk_policy:approval_deny",
  "risk_policy:approval_request",
  "risk_policy:bypass_request_approve",
  "risk_policy:bypass_request_create",
  "risk_policy:bypass_request_deny",
  "risk_policy:bypass_request_revoke",
  "risk_policy:challenge_acknowledge",
  "risk_policy:create",
  "risk_policy:delete",
  "risk_policy:eval_review_delete",
  "risk_policy:eval_review_save",
  "risk_policy:trigger",
  "risk_policy:update",
  "risk_result:dismiss",
  "risk_result:restore",
  "risk_result:reveal_payload",
  "risk_result:unmask",
  "session_quarantine:open",
  "session_quarantine:release",
  "sigint-sensor:create",
  "sigint-sensor:delete",
  "sigint-sensor:update",
  "sigint-signal:create",
  "sigint-signal:delete",
  "sigint-signal:update",
  "skill:add_version",
  "skill:archive",
  "skill:create",
  "skill:distribute",
  "skill:link",
  "skill:restore_version",
  "skill:share_link_create",
  "skill:share_link_revoke",
  "skill:suggestion_approve",
  "skill:suggestion_dismiss",
  "skill:undistribute",
  "skill:unlink",
  "skill:update",
  "skill:update_distribution",
  "skill:update_link",
  "skill_efficacy_settings:upsert",
  "spend_rule:archive",
  "spend_rule:create",
  "spend_rule:update",
  "template:create",
  "template:delete",
  "template:update",
  "toolset:attach_external_oauth",
  "toolset:attach_oauth_proxy",
  "toolset:create",
  "toolset:delete",
  "toolset:detach_external_oauth",
  "toolset:detach_oauth_proxy",
  "toolset:migrate_to_remote_mcp",
  "toolset:update",
  "toolset:update_external_oauth_issuer",
  "toolset:update_oauth_proxy",
  "trigger-instance:create",
  "trigger-instance:delete",
  "trigger-instance:pause",
  "trigger-instance:resume",
  "trigger-instance:update",
  "tunneled-mcp:create",
  "tunneled-mcp:delete",
  "tunneled-mcp:rotate-key",
  "tunneled-mcp:update",
  "unproxied-mcp:create",
  "unproxied-mcp:delete",
  "user-session-client:cimd-refresh",
  "user-session-client:revoke",
  "user-session-consent:revoke",
  "user-session-issuer-cimd-client:add",
  "user-session-issuer-cimd-client:remove",
  "user-session-issuer:create",
  "user-session-issuer:delete",
  "user-session-issuer:migrate",
  "user-session-issuer:update",
  "user-session:revoke",
  "variation:delete_global",
  "variation:update_global",
  "wake:cancelled",
  "wake:fired",
  "wake:scheduled",
  "widget:create",
  "widget:delete",
  "widget:update",
  "workload-admission:admit",
  "workload-admission:update",
  "workload-admission:withdraw",
  "workload-device-join-token:create",
  "workload-device-join-token:redeem",
  "workload-issuer:create",
  "workload-issuer:delete",
  "workload-issuer:update",
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

const AUDIT_ACTION_SET: ReadonlySet<string> = new Set(AUDIT_ACTIONS);

export function isAuditAction(action: string): action is AuditAction {
  return AUDIT_ACTION_SET.has(action);
}

/**
 * Past-tense phrase for an action, written to read as a sentence with the actor
 * in front and the subject display name behind it:
 *
 *   "adam@example.com" + "deleted risk policy" + "Shadow MCP Server Policy"
 *
 * Phrases that dangle a preposition ("... invite for") expect a subject name;
 * the rest read fine either way.
 */
export function staticActionPhrase(action: AuditAction): string {
  switch (action) {
    case "slack-identity-mapping:confirm":
      return "confirmed the Slack mapping for";
    case "slack-identity-mapping:reassign":
      return "reassigned the Slack mapping for";
    case "slack-identity-mapping:reconfirm":
      return "reviewed and confirmed the Slack mapping for";
    case "slack-identity-mapping:unmap":
      return "removed the Slack mapping for";
    case "slack-directory-connection:sync":
      return "synced Slack directory";
    case "slack-directory-connection:authorize":
      return "authorized Slack workspace";
    case "slack-directory-connection:disconnect":
      return "disconnected Slack workspace";
    case "access_challenge:resolve":
      return "resolved access challenge";
    case "access_member:revoke_sign_ins":
      return "revoked sign-ins for";
    case "access_member:update_role":
      return "changed member role for";
    case "access_role:create":
      return "created access role";
    case "access_role:update":
      return "updated access role";
    case "access_role:delete":
      return "deleted access role";

    case "agent:connection_block_create":
      return "blocked connection access for agent";
    case "agent:connection_block_delete":
      return "unblocked connection access for agent";
    case "agent:create":
      return "created agent";
    case "agent:delete":
      return "deleted agent";
    case "agent:policy_grant_create":
      return "added direct policy grant to agent";
    case "agent:policy_grant_delete":
      return "removed direct policy grant from agent";
    case "agent:policy_grant_update":
      return "updated direct policy grant for agent";
    case "agent:owner_loss":
      return "recorded owner loss for agent";
    case "agent:reassign":
      return "reassigned agent";
    case "agent:rename":
      return "renamed agent";
    case "agent:resume":
      return "resumed agent";
    case "agent:revoke":
      return "revoked agent";
    case "agent:suspend":
      return "suspended agent";
    case "agent:transfer":
      return "transferred agent";

    case "ai_integration:upsert":
      return "configured AI integration";
    case "ai_integration:delete":
      return "removed AI integration";
    case "ai_integration:update_schedule":
      return "updated AI integration schedule";
    case "ai_integration:retry_schedule":
      return "retried AI integration sync";

    case "ai_tool_decision:set":
      return "decided AI tool access for";

    case "api_key:create":
      return "created API key";
    case "api_key:revoke":
      return "revoked API key";
    case "api_key:update_policy":
      return "changed the access policy of API key";

    case "asset:create":
      return "uploaded asset";

    case "auth-client:create":
      return "created auth client";
    case "auth-client:update":
      return "renamed auth client";
    case "auth-client:update-headers":
      return "changed the headers of auth client";
    case "auth-client:attach-mcp-server":
      return "added an MCP server to auth client";
    case "auth-client:enable":
      return "enabled auth client";
    case "auth-client:disable":
      return "disabled auth client";
    case "auth-client:detach-mcp-server":
      return "removed an MCP server from auth client";
    case "auth-client:update-audience":
      return "changed who can use auth client";
    case "assistant:identity_provision":
      return "gave a dedicated agent to assistant";
    case "assistant:tool_call":
      return "ran assistant tool";
    case "assistant:update_execution_profile":
      return "changed the upstream credentials of assistant";

    case "aws_iam_credential:create":
      return "added AWS IAM credential";
    case "aws_iam_credential:update":
      return "updated AWS IAM credential";
    case "aws_iam_credential:delete":
      return "removed AWS IAM credential";
    case "aws_kms_key:create":
      return "added AWS KMS key";
    case "aws_kms_key:update":
      return "updated AWS KMS key";
    case "aws_kms_key:delete":
      return "removed AWS KMS key";
    case "gcp_iam_credential:create":
      return "added GCP IAM credential";
    case "gcp_iam_credential:update":
      return "updated GCP IAM credential";
    case "gcp_iam_credential:delete":
      return "removed GCP IAM credential";
    case "gcp_kms_key:create":
      return "added GCP KMS key";
    case "gcp_kms_key:update":
      return "updated GCP KMS key";
    case "gcp_kms_key:delete":
      return "removed GCP KMS key";
    case "identity-provider-connection:create":
      return "connected identity provider";
    case "identity-provider-connection:submit-client-id":
      return "submitted client ID for identity provider";
    case "identity-provider-connection:replace-client-secret":
      return "replaced client secret for identity provider";
    case "identity-provider-connection:set-setup-method":
      return "changed setup method for identity provider";
    case "identity-provider-connection:verify":
      return "verified identity provider connection to";
    case "identity-provider-connection:record-agent":
      return "recorded agent for identity provider";
    case "identity-provider-connection:revoke":
      return "revoked identity provider connection to";
    case "identity-provider-connection:sync-applications":
      return "requested an applications sync for identity provider";
    case "okta-resource-connection:confirm":
      return "confirmed the Cross App Access connection for";
    case "okta-resource-connection:reset":
      return "reset the Cross App Access connection for";
    case "okta-resource-connection:observe":
      return "observed a Cross App Access exchange result for";
    case "okta-server-suggestion:dismiss":
      return "dismissed a suggestion for";
    case "okta-server-suggestion:restore":
      return "restored a suggestion for";
    case "json_web_key_set:create":
      return "created JSON Web Key Set";
    case "json_web_key_set:update":
      return "updated JSON Web Key Set";
    case "json_web_key_set:delete":
      return "deleted JSON Web Key Set";
    case "internal_session_issuer:enable":
      return "enabled Speakeasy signing with issuer";
    case "internal_session_issuer:disable":
      return "disabled Speakeasy signing with issuer";
    case "issuance-selection-profile:create":
      return "set the upstream credentials of headless caller";
    case "issuance-selection-profile:update":
      return "changed the upstream credentials of headless caller";
    case "issuance-selection-profile:delete":
      return "removed the upstream credential choices of headless caller";
    case "json_web_key:publish":
      return "published JSON Web Key";
    case "json_web_key:activate":
      return "activated JSON Web Key";
    case "json_web_key:retire":
      return "retired JSON Web Key";
    case "json_web_key:revoke":
      return "revoked JSON Web Key";
    case "json_web_key:delete":
      return "withdrew JSON Web Key";

    case "billing_metadata:create_stripe_checkout":
      return "started Stripe checkout for";
    case "billing_metadata:create_stripe_portal":
      return "opened Stripe billing portal for";
    case "billing_metadata:cancel_stripe_subscription":
      return "canceled Stripe subscription for";
    case "billing_metadata:resume_stripe_subscription":
      return "resumed Stripe subscription for";
    case "billing_metadata:update":
      return "updated billing metadata";
    case "catalog-promotion:archive":
      return "archived catalog promotion candidate";
    case "catalog-promotion:reject":
      return "rejected catalog promotion candidate";
    case "catalog-promotion:restore":
      return "restored catalog promotion candidate";
    case "catalog-promotion:retry-docs":
      return "retried setup documentation for";
    case "chat_analysis_settings:upsert":
      return "updated chat analysis settings";
    case "chat_session:access":
      return "opened chat session";
    case "chat_session:handoff_export":
      return "exported chat session handoff";
    case "chat_session:move":
      return "moved chat session";
    case "chat_session:recall":
      return "recalled chat session";

    case "custom_domains:create":
      return "added custom domain";
    case "custom_domains:update":
      return "updated custom domain";
    case "custom_domains:delete":
      return "removed custom domain";

    case "data_export_route:create":
      return "created data export route";
    case "data_export_route:update":
      return "updated data export route";
    case "data_export_route:delete":
      return "deleted data export route";
    case "data_export_route:pause":
      return "paused data export route";
    case "data_export_route:resume":
      return "resumed data export route";

    case "network_ingress:create":
      return "created private network ingress";
    case "network_ingress:update":
      return "updated private network ingress";
    case "network_ingress:rotate_credentials":
      return "rotated private network ingress credentials";
    case "network_ingress:delete":
      return "deleted private network ingress";

    case "deployments:create":
      return "created deployment";
    case "deployments:evolve":
      return "updated deployment";
    case "deployments:redeploy":
      return "redeployed deployment";

    case "device_agent_ai_scan_target:create":
      return "added AI scan target";
    case "device_agent_ai_scan_target:update":
      return "updated AI scan target";
    case "device_agent_ai_scan_target:delete":
      return "removed AI scan target";
    case "device_integration:upsert":
      return "configured device integration";
    case "device_integration:delete":
      return "removed device integration";
    case "device_integration:update_schedule":
      return "updated device integration schedule";
    case "device_integration:retry_schedule":
      return "retried device integration sync";

    case "directory_review_item:approve":
      return "approved directory change";
    case "directory_review_item:dismiss":
      return "dismissed directory change";
    case "directory_review_item:opt_out":
      return "stopped review of new attribute values";
    case "directory_review_settings:update":
      return "updated directory review emails";

    case "directory_role_mapping:set":
      return "set directory role mapping";
    case "directory_role_mapping:delete":
      return "removed directory role mapping";

    case "environment:create":
      return "created environment";
    case "environment:update":
      return "updated environment";
    case "environment:delete":
      return "deleted environment";

    case "killswitch:activate":
      return "activated killswitch";
    case "killswitch:change":
      return "changed killswitch";
    case "killswitch:deactivate":
      return "deactivated killswitch";
    case "killswitch:expire":
      return "recorded killswitch expiry";

    case "litellm_instance:create":
      return "created LiteLLM instance";
    case "litellm_instance:rotate_key":
      return "rotated LiteLLM instance key";
    case "litellm_instance:revoke":
      return "revoked LiteLLM instance";

    case "mcp-endpoint:create":
      return "created MCP endpoint";
    case "mcp-endpoint:update":
      return "updated MCP endpoint";
    case "mcp-endpoint:delete":
      return "deleted MCP endpoint";

    case "meta-mcp:create":
      return "created meta MCP server";
    case "meta-mcp:update":
      return "updated meta MCP server";
    case "meta-mcp:delete":
      return "deleted meta MCP server";
    case "meta-mcp:add_member":
      return "added meta MCP member";
    case "meta-mcp:update_member":
      return "updated meta MCP member";
    case "meta-mcp:remove_member":
      return "removed meta MCP member";

    case "mcp-server:promote-catalogue":
      return "published to the MCP catalog";
    case "mcp-server:create":
      return "created MCP server";
    case "mcp-server:update":
      return "updated MCP server";
    case "mcp-server:delete":
      return "deleted MCP server";
    case "mcp-server:update-scope-pin":
      return "updated pinned scopes on MCP server";
    case "mcp-server:update-tool-metadata":
      return "updated tool metadata on MCP server";
    case "mcp-server:update-upstream-auth-requirement":
      return "changed whether the upstream needs authentication on MCP server";

    case "mcp_approval_request:approve":
      return "approved MCP access to";
    case "mcp_approval_request:create":
      return "requested MCP access to";
    case "mcp_approval_request:deny":
      return "denied MCP access to";
    case "mcp_approval_request:evidence_changed":
      return "detected changed evidence for approved MCP server";
    case "mcp_approval_request:research_start":
      return "started research on";
    case "mcp_approval_request:supersede":
      return "superseded the access decision for";

    case "mcp_metadata:update":
      return "updated MCP metadata for";

    case "model_provider_key:upsert":
      return "updated model provider key";
    case "model_provider_key:delete":
      return "removed model provider key";

    case "openrouter-key:disable":
      return "disabled platform OpenRouter key";
    case "openrouter-key:enable":
      return "enabled platform OpenRouter key";
    case "openrouter-key:set_spend_cap":
      return "changed inference cap for";

    case "organization:enabled":
      return "enabled organization access for";
    case "organization:disabled":
      return "disabled organization access for";
    case "organization:whitelist_updated":
      return "changed demo-access whitelisting for";
    case "organization:webhooks_enabled":
      return "enabled webhook delivery";
    case "organization:webhooks_disabled":
      return "disabled webhook delivery";
    case "organization:hooks_fail_open_enabled":
      return "enabled fail-open for hooks";
    case "organization:hooks_fail_open_disabled":
      return "disabled fail-open for hooks";
    case "organization:device_agent_configuration_updated":
      return "updated device agent configuration";
    case "organization:account_type_changed":
      return "changed account type for";
    case "organization:inference_key_repaired":
      return "repaired inference key for";
    case "organization:enterprise_trial_armed":
      return "started enterprise trial";
    case "organization:enterprise_trial_demoted":
      return "ended enterprise trial";
    case "organization:enterprise_trial_end_changed":
      return "changed enterprise trial end date";
    case "organization:enterprise_trial_extended":
      return "extended enterprise trial";
    case "organization:enterprise_trial_rearmed":
      return "restarted enterprise trial";
    case "organization:enterprise_trial_started":
      return "started a new enterprise trial";
    case "organization:enterprise_trial_converted":
      return "converted enterprise trial for";
    case "organization:partner_signup_attributed":
      return "attributed a partner signup to";
    case "organization:promotion_redeemed":
      return "redeemed a promotion for";
    case "organization:payg_activated":
      return "activated pay-as-you-go billing for";
    case "organization:payg_deactivated":
      return "deactivated pay-as-you-go billing for";
    case "organization:product_feature_enabled":
      return "enabled a product feature for";
    case "organization:product_feature_disabled":
      return "disabled a product feature for";
    case "organization:setup_task_updated":
      return "updated setup task for";
    case "organization:onboarding_updated":
      return "updated onboarding for";
    case "organization:onboarding_stack_updated":
      return "updated the onboarding stack for";
    case "organization:onboarding_playbook_assigned":
      return "assigned an onboarding playbook to";
    case "organization:onboarding_playbook_unassigned":
      return "removed the onboarding playbook from";

    case "organization_invitation:create":
      return "invited";
    case "organization_invitation:revoke":
      return "revoked invite for";
    case "organization_invitation:update_role":
      return "changed invite role for";

    case "otel_destination:create":
      return "created OpenTelemetry destination";
    case "otel_destination:update":
      return "updated OpenTelemetry destination";
    case "otel_destination:delete":
      return "deleted OpenTelemetry destination";

    case "platform-mcp-diagnostics:attribution_read":
      return "read usage attribution for";
    case "platform-mcp-diagnostics:user_status_read":
      return "read a user's status on";

    case "platform-mcp-registration:create":
      return "registered platform MCP server";
    case "platform-mcp-registration:handoff_issue":
      return "issued a registration handoff for";
    case "platform-mcp-registration:handoff_redeem":
      return "redeemed a registration handoff for";

    case "plugin:create":
      return "created plugin";
    case "plugin:update":
      return "updated plugin";
    case "plugin:delete":
      return "deleted plugin";
    case "plugin:server_add":
      return "added a server to plugin";
    case "plugin:server_update":
      return "updated a server on plugin";
    case "plugin:server_remove":
      return "removed a server from plugin";
    case "plugin:assignments_set":
      return "updated plugin access";
    case "plugin:publish":
      return "published plugins";
    case "plugin:marketplace_settings_update":
      return "updated marketplace settings for";

    case "project:create":
      return "created project";
    case "project:update":
      return "updated project";
    case "project:delete":
      return "deleted project";

    // Saved queries were replaced by widgets; these stay so audit rows
    // written before the change still read well.
    case "query:create":
      return "created saved query";
    case "query:update":
      return "updated saved query";
    case "query:delete":
      return "deleted saved query";
    case "widget:create":
      return "created widget";
    case "widget:update":
      return "updated widget";
    case "widget:delete":
      return "deleted widget";
    case "dashboard:create":
      return "created dashboard";
    case "dashboard:update":
      return "updated dashboard";
    case "dashboard:layout":
      return "laid out dashboard";
    case "dashboard:delete":
      return "deleted dashboard";

    case "remote-mcp:create":
      return "added remote MCP server";
    case "remote-mcp:update":
      return "updated remote MCP server";
    case "remote-mcp:delete":
      return "removed remote MCP server";
    case "remote-mcp-server-header:create":
      return "added a header to remote MCP server";
    case "remote-mcp-server-header:update":
      return "updated a header on remote MCP server";
    case "remote-mcp-server-header:delete":
      return "removed a header from remote MCP server";

    case "remote-session:attach":
      return "attached a binding to";
    case "remote-session:detach":
      return "detached a binding from";
    case "remote-session:refresh":
      return "refreshed remote session";
    case "remote-session:delete":
      return "deleted remote session";
    case "remote-session-client:create":
      return "created remote session client";
    case "remote-session-client:duplicate":
      return "duplicated remote session client";
    case "remote-session-client:update":
      return "updated remote session client";
    case "remote-session-client:delete":
      return "deleted remote session client";
    case "remote-session-client:attach-user-session-issuer":
      return "attached a user session issuer to";
    case "remote-session-client:detach-user-session-issuer":
      return "detached a user session issuer from";
    case "remote-session-client:attach-json-web-key-set":
      return "attached a JSON Web Key Set to";
    case "remote-session-client:detach-json-web-key-set":
      return "detached a JSON Web Key Set from";
    case "remote-session-client:detach-mcp-server":
      return "detached an MCP server from";
    case "remote-session-client:revoke-sessions":
      return "revoked sessions for";
    case "remote-session-client:enable-identity-chaining":
      return "enabled identity chaining for";
    case "remote-session-client:update-identity-chaining-scopes":
      return "updated identity chaining scopes for";
    case "remote-session-client:disable-identity-chaining":
      return "disabled identity chaining for";
    case "remote-session-issuer:create":
      return "created remote session issuer";
    case "remote-session-issuer:update":
      return "updated remote session issuer";
    case "remote-session-issuer:delete":
      return "deleted remote session issuer";
    case "remote-session-issuer:update_ema_providers":
      return "changed the enabled identity providers for";
    case "remote-session-issuer:migrate":
      return "migrated remote session issuer";

    case "risk_exclusion:create":
      return "created risk exclusion";
    case "risk_exclusion:update":
      return "updated risk exclusion";
    case "risk_exclusion:delete":
      return "deleted risk exclusion";

    case "risk_policy:create":
      return "created risk policy";
    case "risk_policy:update":
      return "updated risk policy";
    case "risk_policy:delete":
      return "deleted risk policy";
    case "risk_policy:trigger":
      return "triggered risk policy";
    case "risk_policy:approval_request":
      return "requested approval of a tool call under";
    case "risk_policy:approval_approve":
      return "approved a held tool call under";
    case "risk_policy:approval_deny":
      return "denied a held tool call under";
    case "risk_policy:bypass_request_create":
      return "requested a bypass for";
    case "risk_policy:bypass_request_approve":
      return "approved a bypass for";
    case "risk_policy:bypass_request_deny":
      return "denied a bypass for";
    case "risk_policy:bypass_request_revoke":
      return "revoked a bypass for";
    case "risk_policy:challenge_acknowledge":
      return "acknowledged a challenge for";
    case "risk_policy:eval_review_save":
      return "saved an evaluation review for";
    case "risk_policy:eval_review_delete":
      return "deleted an evaluation review for";

    case "risk_result:dismiss":
      return "dismissed risk finding";
    case "risk_result:restore":
      return "restored risk finding";
    case "risk_result:reveal_payload":
      return "revealed risk finding payload";
    case "risk_result:unmask":
      return "unmasked risk finding";

    case "session_quarantine:open":
      return "quarantined agent session";
    case "session_quarantine:release":
      return "released quarantined agent session";

    case "sigint-sensor:create":
      return "created signals intelligence sensor";
    case "sigint-sensor:update":
      return "updated signals intelligence sensor";
    case "sigint-sensor:delete":
      return "deleted signals intelligence sensor";
    case "sigint-signal:create":
      return "created signals intelligence signal";
    case "sigint-signal:update":
      return "updated signals intelligence signal";
    case "sigint-signal:delete":
      return "deleted signals intelligence signal";

    case "skill:create":
      return "created skill";
    case "skill:update":
      return "updated skill";
    case "skill:archive":
      return "archived skill";
    case "skill:add_version":
      return "added a version to skill";
    case "skill:restore_version":
      return "restored a version of skill";
    case "skill:distribute":
      return "distributed skill";
    case "skill:undistribute":
      return "stopped distributing skill";
    case "skill:update_distribution":
      return "updated distribution for skill";
    case "skill:link":
      return "linked skill";
    case "skill:update_link":
      return "updated a link for skill";
    case "skill:unlink":
      return "unlinked skill";
    case "skill:share_link_create":
      return "created a share link for";
    case "skill:share_link_revoke":
      return "revoked a share link for";
    case "skill:suggestion_approve":
      return "approved a suggestion for";
    case "skill:suggestion_dismiss":
      return "dismissed a suggestion for";
    case "skill_efficacy_settings:upsert":
      return "updated skill efficacy settings";

    case "spend_rule:create":
      return "created spend rule";
    case "spend_rule:update":
      return "updated spend rule";
    case "spend_rule:archive":
      return "archived spend rule";

    case "template:create":
      return "created template";
    case "template:update":
      return "updated template";
    case "template:delete":
      return "deleted template";

    case "toolset:create":
      return "created MCP server";
    case "toolset:update":
      return "updated MCP server";
    case "toolset:delete":
      return "deleted MCP server";
    case "toolset:attach_external_oauth":
      return "connected an external OAuth server to";
    case "toolset:detach_external_oauth":
      return "disconnected an external OAuth server from";
    case "toolset:update_external_oauth_issuer":
      return "updated the external OAuth issuer for";
    case "toolset:attach_oauth_proxy":
      return "attached an OAuth proxy to";
    case "toolset:update_oauth_proxy":
      return "updated the OAuth proxy on";
    case "toolset:detach_oauth_proxy":
      return "detached an OAuth proxy from";
    case "toolset:migrate_to_remote_mcp":
      return "migrated the external MCP server";

    case "trigger-instance:create":
      return "created trigger";
    case "trigger-instance:update":
      return "updated trigger";
    case "trigger-instance:delete":
      return "deleted trigger";
    case "trigger-instance:pause":
      return "paused trigger";
    case "trigger-instance:resume":
      return "resumed trigger";
    case "wake:scheduled":
      return "scheduled a wake for";
    case "wake:fired":
      return "ran a scheduled wake for";
    case "wake:cancelled":
      return "cancelled a wake for";

    case "tunneled-mcp:create":
      return "added tunneled MCP server";
    case "tunneled-mcp:update":
      return "updated tunneled MCP server";
    case "tunneled-mcp:delete":
      return "removed tunneled MCP server";
    case "tunneled-mcp:rotate-key":
      return "rotated the key for tunneled MCP server";
    case "unproxied-mcp:create":
      return "added unproxied MCP server";
    case "unproxied-mcp:delete":
      return "removed unproxied MCP server";

    case "user-session:revoke":
      return "revoked user session";
    case "user-session-client:cimd-refresh":
      return "refreshed the client metadata document for";
    case "user-session-client:revoke":
      return "revoked user session client";
    case "user-session-consent:revoke":
      return "revoked consent for";
    case "user-session-issuer:create":
      return "created user session issuer";
    case "user-session-issuer:update":
      return "updated user session issuer";
    case "user-session-issuer:delete":
      return "deleted user session issuer";
    case "user-session-issuer:migrate":
      return "migrated user session issuer";
    case "user-session-issuer-cimd-client:add":
      return "added a CIMD client to";
    case "user-session-issuer-cimd-client:remove":
      return "removed a CIMD client from";

    case "variation:update_global":
      return "updated a global variation for";
    case "variation:delete_global":
      return "deleted a global variation for";

    case "workload-issuer:create":
      return "started trusting workload issuer";
    case "workload-issuer:update":
      return "updated workload issuer";
    case "workload-issuer:delete":
      return "stopped trusting workload issuer";
    // Named for what they do, because the row is the grant of machine access
    // rather than a record about one.
    case "workload-admission:admit":
      return "admitted workload";
    case "workload-admission:update":
      return "updated workload";
    case "workload-admission:withdraw":
      return "withdrew workload";
    case "workload-device-join-token:create":
      return "created a device join token for";
    case "workload-device-join-token:redeem":
      return "enrolled a device with a join token for";

    default:
      return assertNever(action);
  }
}
